import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    LogOut, RefreshCw, Trash2, Inbox, Loader2,
    Mail, Phone, ChevronDown, ChevronUp, Download,
} from "lucide-react";
import { adminAxios, getAdminToken, clearAdminToken, formatApiError } from "@/lib/adminApi";
import { LOGO_URL } from "@/lib/brand";
import { useNoIndex } from "@/lib/useNoIndex";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel,
    AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
    AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function fmt(iso) {
    try {
        return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return iso; }
}

const SECTIONS = [
    { title: "About the Business", fields: [
        ["Products / Services", "products_services"],
        ["Time in Business", "time_in_business"],
        ["Ideal Customer", "ideal_customer"],
        ["What Makes Them Unique", "unique_value"],
    ]},
    { title: "Project Overview", fields: [
        ["Website Type", "website_type"],
        ["Primary Purpose", "website_purpose"],
        ["Top Goals", "top_goals"],
    ]},
    { title: "Design Preferences", fields: [
        ["Look & Feel", "look_and_feel"],
        ["Reference Sites", "reference_sites"],
        ["Has Branding", "has_branding"],
        ["Colors/Styles to Avoid", "colors_to_avoid"],
    ]},
    { title: "Features", fields: [["Requested Features", "features"]] },
    { title: "Content", fields: [
        ["Has Existing Content", "has_content"],
        ["Content Provision", "content_provision"],
    ]},
    { title: "Pages Needed", fields: [["Pages", "pages_needed"]] },
    { title: "Domain & Hosting", fields: [
        ["Owns Domain", "owns_domain"],
        ["Has Hosting", "has_hosting"],
        ["Needs Help", "needs_domain_help"],
    ]},
    { title: "SEO", fields: [
        ["Service Areas", "service_areas"],
        ["Target Keywords", "target_keywords"],
        ["Main Competitors", "competitors"],
    ]},
    { title: "Budget & Timeline", fields: [
        ["Budget", "budget"],
        ["Completion Timeline", "completion_timeline"],
        ["Launch Date", "launch_date"],
    ]},
    { title: "Ongoing Services", fields: [["Interested In", "ongoing_services"]] },
    { title: "Additional Info", fields: [["Notes", "additional_info"]] },
];

function renderVal(v) {
    if (v == null) return "—";
    if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
    const s = String(v).trim();
    return s || "—";
}

function RowCard({ r, onDelete }) {
    const [open, setOpen] = useState(false);
    return (
        <article data-testid={`q-row-${r.id}`} className="relative bg-black hover:bg-[#0A0A0A] transition-colors duration-500 p-6 md:p-8">
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-display text-2xl text-white leading-tight">{r.name}</h3>
                        <span className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] border border-[#D4AF37]/30 px-2 py-1">{r.business_name}</span>
                    </div>
                    <div className="mt-4 grid sm:grid-cols-2 gap-3">
                        <a href={`mailto:${r.email}`} className="flex items-center gap-2 text-sm text-[#C4C4C4] hover:text-[#D4AF37]">
                            <Mail className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />{r.email}
                        </a>
                        <a href={`tel:${r.phone}`} className="flex items-center gap-2 text-sm text-[#C4C4C4] hover:text-[#D4AF37]">
                            <Phone className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />{r.phone}
                        </a>
                    </div>
                    <div className="mt-3 text-[10px] uppercase tracking-[0.22em] text-[#888]">
                        Preferred contact: <span className="text-[#C4C4C4]">{renderVal(r.preferred_contact)}</span>
                    </div>
                </div>
                <div className="text-right flex flex-col items-end gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#666]">Received</div>
                        <div className="text-xs text-[#C4C4C4] mt-1">{fmt(r.created_at)}</div>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 px-2 py-1 transition-all">
                            {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                            {open ? "Collapse" : "View All"}
                        </button>
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <button className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#888] border border-transparent hover:border-red-500/30 hover:text-red-300 px-2 py-1 transition-all">
                                    <Trash2 className="w-3 h-3" />Delete
                                </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-[#0A0A0A] border border-white/[0.08] text-white">
                                <AlertDialogHeader>
                                    <AlertDialogTitle className="font-display text-2xl">Delete this questionnaire?</AlertDialogTitle>
                                    <AlertDialogDescription className="text-[#C4C4C4] text-sm">
                                        {r.name} · {r.business_name}. This cannot be undone.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel className="bg-transparent border border-white/15 text-white hover:bg-white/5 hover:text-white">Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => onDelete(r.id)} className="bg-red-600 text-white hover:bg-red-500">Delete</AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </div>
                </div>
            </div>

            {open ? (
                <div className="mt-6 pt-6 border-t border-white/[0.06] space-y-6">
                    {r.business_address ? (
                        <div className="text-sm"><div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-1">Business Address</div><div className="text-[#C4C4C4]">{r.business_address}</div></div>
                    ) : null}
                    {r.current_website ? (
                        <div className="text-sm"><div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-1">Current Website</div><div className="text-[#C4C4C4]">{r.current_website}</div></div>
                    ) : null}
                    {SECTIONS.map((sec) => (
                        <div key={sec.title}>
                            <div className="font-display text-lg text-white mb-3">{sec.title}</div>
                            <div className="space-y-3">
                                {sec.fields.map(([label, key]) => (
                                    <div key={key} className="grid sm:grid-cols-[220px_1fr] gap-3 py-2 border-b border-white/[0.04]">
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">{label}</div>
                                        <div className="text-sm text-[#C4C4C4] whitespace-pre-wrap">{renderVal(r[key])}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            ) : null}
        </article>
    );
}

export default function AdminQuestionnaire() {
    useNoIndex();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        if (!getAdminToken()) { navigate("/admin/login", { replace: true }); return; }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await adminAxios.get("/admin/questionnaire");
                if (!cancelled) setItems(data || []);
            } catch (err) {
                if (!cancelled && err?.response?.status !== 401) {
                    toast.error(formatApiError(err?.response?.data?.detail));
                }
            } finally {
                if (!cancelled) { setLoading(false); setRefreshing(false); }
            }
        })();
        return () => { cancelled = true; };
    }, [navigate, reloadKey]);

    const refresh = () => { setRefreshing(true); setReloadKey((k) => k + 1); };
    const [exporting, setExporting] = useState(false);
    const exportCsv = async () => {
        setExporting(true);
        try {
            const { data, headers } = await adminAxios.get("/admin/questionnaire/export", { responseType: "blob" });
            const match = /filename="?([^"]+)"?/.exec(headers["content-disposition"] || "");
            const filename = match ? match[1] : `recoverydesk-questionnaires-${Date.now()}.csv`;
            const url = URL.createObjectURL(new Blob([data], { type: "text/csv;charset=utf-8" }));
            const a = document.createElement("a");
            a.href = url; a.download = filename; document.body.appendChild(a); a.click();
            a.remove(); URL.revokeObjectURL(url);
            toast.success("CSV downloaded.");
        } catch (err) {
            if (err?.response?.status !== 401) toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setExporting(false);
        }
    };
    const deleteOne = async (id) => {
        try { await adminAxios.delete(`/admin/questionnaire/${id}`); setItems((p) => p.filter((x) => x.id !== id)); toast.success("Deleted."); }
        catch (err) { if (err?.response?.status !== 401) toast.error(formatApiError(err?.response?.data?.detail)); }
    };
    const clearAll = async () => {
        try { const { data } = await adminAxios.delete("/admin/questionnaire"); setItems([]); toast.success(`Cleared ${data?.deleted ?? "all"}.`); }
        catch (err) { if (err?.response?.status !== 401) toast.error(formatApiError(err?.response?.data?.detail)); }
    };
    const logout = () => { clearAdminToken(); navigate("/admin/login", { replace: true }); };

    if (!getAdminToken()) return null;

    return (
        <main data-testid="admin-questionnaire-page" className="relative min-h-screen bg-black text-white font-body">
            <header className="sticky top-0 z-40 bg-black/85 backdrop-blur-xl border-b border-white/[0.05]">
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <img src={LOGO_URL} alt="RecoveryDesk" className="h-10 w-10 object-cover rounded-sm ring-1 ring-[#D4AF37]/30" />
                        <div>
                            <div className="font-display text-lg tracking-wide text-silver-gold leading-none">RecoveryDesk</div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mt-1">Admin · Questionnaires</div>
                        </div>
                        <nav className="hidden lg:flex items-center gap-6 ml-8">
                            <button
                                data-testid="admin-tab-questionnaire"
                                onClick={() => navigate("/admin/questionnaire")}
                                className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37]"
                            >
                                Questionnaires
                            </button>
                            <button
                                data-testid="admin-tab-spreadsheets"
                                onClick={() => navigate("/admin/spreadsheets")}
                                className="text-[11px] uppercase tracking-[0.22em] text-[#C4C4C4] hover:text-[#D4AF37] transition-colors"
                            >
                                Spreadsheets
                            </button>
                        </nav>
                    </div>
                    <div className="flex items-center gap-3">
                        <button onClick={refresh} disabled={refreshing || loading} className="inline-flex items-center gap-2 border border-white/10 text-[#C4C4C4] hover:text-[#D4AF37] hover:border-[#D4AF37]/40 px-4 py-2 text-[11px] uppercase tracking-[0.2em] transition-all disabled:opacity-60">
                            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.5} />Refresh
                        </button>
                        {items.length > 0 ? (
                            <button
                                data-testid="admin-export-csv-btn"
                                onClick={exportCsv}
                                disabled={exporting}
                                className="inline-flex items-center gap-2 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/[0.08] px-4 py-2 text-[11px] uppercase tracking-[0.2em] transition-all disabled:opacity-60"
                            >
                                <Download className={`w-3.5 h-3.5 ${exporting ? "animate-pulse" : ""}`} strokeWidth={1.5} />
                                {exporting ? "Exporting…" : "Export CSV"}
                            </button>
                        ) : null}
                        {items.length > 0 ? (
                            <AlertDialog>
                                <AlertDialogTrigger asChild>
                                    <button className="inline-flex items-center gap-2 border border-red-500/40 text-red-300 hover:bg-red-500/10 hover:border-red-500/70 px-4 py-2 text-[11px] uppercase tracking-[0.2em] transition-all">
                                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />Clear All
                                    </button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="bg-[#0A0A0A] border border-white/[0.08] text-white">
                                    <AlertDialogHeader>
                                        <AlertDialogTitle className="font-display text-2xl">Delete all questionnaires?</AlertDialogTitle>
                                        <AlertDialogDescription className="text-[#C4C4C4] text-sm">
                                            This permanently removes every questionnaire from your inbox ({items.length} total). Cannot be undone.
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel className="bg-transparent border border-white/15 text-white hover:bg-white/5 hover:text-white">Cancel</AlertDialogCancel>
                                        <AlertDialogAction onClick={clearAll} className="bg-red-600 text-white hover:bg-red-500">Delete All</AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        ) : null}
                        <button onClick={logout} className="inline-flex items-center gap-2 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/[0.08] px-4 py-2 text-[11px] uppercase tracking-[0.2em] transition-all">
                            <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />Sign Out
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
                <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="gold-rule-solid w-10" />
                            <span className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold">Inbox</span>
                        </div>
                        <h1 className="font-display text-4xl md:text-5xl text-white tracking-tight leading-tight">Client Questionnaires</h1>
                    </div>
                    <div className="font-display text-3xl md:text-4xl text-gold-metallic">
                        {items.length}
                        <span className="text-sm text-[#888] font-body uppercase tracking-[0.25em] ml-3">total</span>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-32 text-[#888]">
                        <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37] mr-3" />Loading…
                    </div>
                ) : items.length === 0 ? (
                    <div className="border border-white/[0.06] bg-[#050505] py-24 text-center">
                        <Inbox className="w-10 h-10 text-[#D4AF37]/60 mx-auto mb-6" strokeWidth={1.25} />
                        <h3 className="font-display text-2xl text-white mb-2">No questionnaires yet.</h3>
                        <p className="text-sm text-[#888] max-w-md mx-auto">
                            When clients complete the questionnaire, their responses will appear here.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-px bg-[#D4AF37]/10">
                        {items.map((r) => <RowCard key={r.id} r={r} onDelete={deleteOne} />)}
                    </div>
                )}
            </div>
        </main>
    );
}
